import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { ReportsTable } from '@/admin/components/ReportsTable'
import { useReports } from '@/admin/hooks'
import { ReportStatus, ReportType } from '@/admin/types'
import { AlertCircle, ArrowLeft, Filter } from 'lucide-react'

export const Route = createFileRoute('/admin/reports')({
    component: ReportsPage,
})

function ReportsPage() {
    const [page, setPage] = useState(0)
    const [statusFilter, setStatusFilter] = useState<ReportStatus | ''>('PENDING')
    const [typeFilter, setTypeFilter] = useState<ReportType | ''>('')
    const limit = 20

    const { data, isLoading, error } = useReports({
        limit,
        offset: page * limit,
        status: statusFilter || undefined,
        reportType: typeFilter || undefined,
    })

    const handleNextPage = () => {
        if (data?.hasMore) {
            setPage((prev) => prev + 1)
        }
    }

    const handlePreviousPage = () => {
        if (page > 0) {
            setPage((prev) => prev - 1)
        }
    }

    const handleStatusFilterChange = (status: ReportStatus | '') => {
        setStatusFilter(status)
        setPage(0)
    }

    const handleTypeFilterChange = (type: ReportType | '') => {
        setTypeFilter(type)
        setPage(0)
    }

    // Calculate pending count from current filter or show badge
    const pendingCount = statusFilter === 'PENDING' ? data?.total : undefined

    return (
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            {/* Header with back button */}
            <div className="mb-8">
                <Link
                    to="/admin/dashboard"
                    className="mb-4 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Dashboard
                </Link>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="flex items-center gap-3 text-3xl font-bold text-gray-900">
                            Reports Queue
                            {pendingCount !== undefined && pendingCount > 0 && (
                                <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-800">
                                    {pendingCount} pending
                                </span>
                            )}
                        </h1>
                        <p className="mt-2 text-gray-600">
                            Review and moderate user reports. Apple requires action within 24 hours.
                        </p>
                    </div>
                </div>
            </div>

            {/* Apple compliance notice */}
            <div className="mb-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 shrink-0 text-yellow-600" />
                    <div>
                        <h3 className="font-medium text-yellow-800">24-Hour Response Requirement</h3>
                        <p className="mt-1 text-sm text-yellow-700">
                            Per Apple App Store Guideline 1.2, all reports must be reviewed and actioned within 24 hours.
                            Take appropriate action: hide objectionable content and ban offending users.
                        </p>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="mb-6 flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Filter className="h-4 w-4" />
                    Filters:
                </div>
                <div>
                    <label htmlFor="status" className="sr-only">
                        Status
                    </label>
                    <select
                        id="status"
                        value={statusFilter}
                        onChange={(e) => handleStatusFilterChange(e.target.value as ReportStatus | '')}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                        <option value="">All Statuses</option>
                        <option value="PENDING">Pending</option>
                        <option value="UNDER_REVIEW">Under Review</option>
                        <option value="RESOLVED">Resolved</option>
                        <option value="DISMISSED">Dismissed</option>
                    </select>
                </div>
                <div>
                    <label htmlFor="type" className="sr-only">
                        Type
                    </label>
                    <select
                        id="type"
                        value={typeFilter}
                        onChange={(e) => handleTypeFilterChange(e.target.value as ReportType | '')}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                        <option value="">All Types</option>
                        <option value="USER">Users</option>
                        <option value="PRODUCT">Products</option>
                        <option value="LOOK">Looks</option>
                        <option value="COLLECTION">Collections</option>
                    </select>
                </div>
            </div>

            {/* Content */}
            {error ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
                    <div className="mb-2 text-4xl">⚠️</div>
                    <h2 className="mb-2 text-xl font-semibold text-red-900">
                        Error Loading Reports
                    </h2>
                    <p className="text-red-700">
                        {error instanceof Error ? error.message : 'Failed to load reports'}
                    </p>
                </div>
            ) : (
                <ReportsTable
                    reports={data?.reports ?? []}
                    isLoading={isLoading}
                    total={data?.total ?? 0}
                    hasMore={data?.hasMore ?? false}
                    page={page}
                    onNextPage={handleNextPage}
                    onPreviousPage={handlePreviousPage}
                />
            )}
        </div>
    )
}
